/**
 * Client-side runbook resolver.
 *
 * Reads pre-resolved values from window.__RUNBOOK_CONFIG__ which is injected
 * by the RunbookConfig server component in layout.tsx.
 *
 * Usage:
 *   import { resolveRunbook, useRunbook } from '@/lib/runbook/client';
 *   const label = resolveRunbook('ui-frontend.shell.header.subtitle', 'Default Text');
 *   // Or as a hook (recommended — handles hydration and runtime switching):
 *   const label = useRunbook('ui-frontend.shell.header.subtitle', 'Default Text');
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Resolve a runbook namespace on the client side.
 *
 * Returns the override value from window.__RUNBOOK_CONFIG__ if available,
 * otherwise returns the provided default value.
 *
 * During SSR (typeof window === 'undefined'), always returns the default.
 * The RunbookConfig script tag in <head> ensures window.__RUNBOOK_CONFIG__
 * is set before React hydration, minimizing any visual flash.
 *
 * NOTE: When used inline in JSX with suppressHydrationWarning, React 18 may
 * keep the server-rendered text and never update it. Use useRunbook() instead
 * for text that must reflect the override after hydration.
 */
export function resolveRunbook(namespace: string, defaultValue: string): string {
  if (typeof window === 'undefined') return defaultValue;
  const config = window.__RUNBOOK_CONFIG__;
  if (!config || !(namespace in config)) return defaultValue;
  const value = config[namespace];
  return typeof value === 'string' ? value : defaultValue;
}

/**
 * React hook for runbook values that handles SSR/hydration and runtime switching.
 *
 * Renders the default during SSR and initial hydration (no mismatch),
 * then updates to the runbook override after mount via useEffect.
 *
 * Listens for 'runbook-config-changed' events (dispatched by RunbookConfig
 * server component after router.refresh()) so values update instantly when
 * an admin switches runbook mode — no rebuild or page reload needed.
 */
export function useRunbook(namespace: string, defaultValue: string): string {
  const [value, setValue] = useState(defaultValue);

  const resolve = useCallback(() => {
    setValue(resolveRunbook(namespace, defaultValue));
  }, [namespace, defaultValue]);

  // Initial mount — pick up __RUNBOOK_CONFIG__ after hydration
  useEffect(resolve, [resolve]);

  // Re-resolve when runbook mode is switched at runtime
  useEffect(() => {
    window.addEventListener('runbook-config-changed', resolve);
    return () => window.removeEventListener('runbook-config-changed', resolve);
  }, [resolve]);

  return value;
}
